import { supabase } from '../supabase/client';
import { TeamPreferences, UpdateTeamPreferencesRequest } from '@/types/preference';

export const preferenceService = {
  /**
   * Get team preferences
   */
  async getTeamPreferences(teamId: string): Promise<TeamPreferences> {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }
    
    const { data, error } = await supabase
      .from('team_preferences')
      .select('*')
      .eq('team_id', teamId)
      .single();

    if (error) {
      // If no preferences exist yet, create default preferences
      if (error.code === 'PGRST116') {
        return this.createDefaultTeamPreferences(teamId);
      }
      throw new Error(`Failed to fetch team preferences: ${error.message}`);
    }

    return data;
  },

  /**
   * Create default team preferences
   */
  async createDefaultTeamPreferences(teamId: string): Promise<TeamPreferences> {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }
    
    const defaultPreferences = {
      team_id: teamId,
      scheduling_preferences: {
        auto_publish_schedule: false,
        provider_preference_deadline_days: 14,
        weekend_distribution_strategy: 'equal',
        honor_request_days_off_priority: 'high',
        enforce_minimum_rest_period: true,
        enable_night_shift_rotation: false,
        custom_scheduling_rules: {}
      },
      notification_preferences: {
        email_notifications: true,
        schedule_publication_notification: true,
        preference_submission_reminder: true,
        shift_trade_notifications: true,
        advance_schedule_reminder_days: 7,
        custom_notification_settings: {}
      }
    };

    const { data, error } = await supabase
      .from('team_preferences')
      .insert(defaultPreferences)
      .select('*')
      .single();

    if (error) {
      throw new Error(`Failed to create default team preferences: ${error.message}`);
    }

    return data;
  },

  /**
   * Update team preferences
   */
  async updateTeamPreferences(request: UpdateTeamPreferencesRequest): Promise<TeamPreferences> {
    if (!supabase) {
      throw new Error('Supabase client is not initialized');
    }
    
    const { team_id, scheduling_preferences, notification_preferences } = request;
    
    // Get current preferences first
    const currentPreferences = await this.getTeamPreferences(team_id);
    
    // Prepare update data
    const updateData: Record<string, object> = {};
    
    if (scheduling_preferences) {
      updateData.scheduling_preferences = {
        ...currentPreferences.scheduling_preferences,
        ...scheduling_preferences
      };
    }
    
    if (notification_preferences) {
      updateData.notification_preferences = {
        ...currentPreferences.notification_preferences,
        ...notification_preferences
      };
    }
    
    // Update only if there's something to update
    if (Object.keys(updateData).length === 0) {
      return currentPreferences;
    }
    
    const { data, error } = await supabase
      .from('team_preferences')
      .update(updateData)
      .eq('team_id', team_id)
      .select('*')
      .single();

    if (error) {
      throw new Error(`Failed to update team preferences: ${error.message}`);
    }

    return data;
  }
}; 